<script setup>
import { onMounted, computed, watch, ref } from "vue";
import { useRouter } from "vue-router";
import moment from "moment";

import Filters from "@/components/job-position/filters/Filters.vue";

import debounce from "lodash/debounce";
import { useJobPositionStore } from "@/stores";

const jobPositionStore = useJobPositionStore();

const router = useRouter();

const data = computed(() => jobPositionStore.overview);

const params = computed(() => jobPositionStore.requestQueryParams);

const pagination = computed(() => jobPositionStore.pagination);

const isFetching = ref(false);

const formatDateTime = (date) => {
  if (date === null) {
    return date;
  }
  return moment(date, "YYYY-MM-DDTHH:mm:ss").format("DD-MM-YYYY HH:mm");
};

const getJobPositions = async () => {
  isFetching.value = true;

  params.value.set("page", pagination.value.currentPage - 1);
  params.value.set("size", pagination.value.pageSize);

  await jobPositionStore.getJobPositionOverview(params.value);

  isFetching.value = false;
};

const openJobAd = (link) => {
  window.open(link, "_blank");
};

const checkClickEvent = (event) => {
  const check = event.target && event.target.classList.contains("mdi-pencil");
  return check;
};

const download = ref(false);

const exportTable = async () => {
  download.value = true;
  const resp = await jobPositionStore.downloadExcel(params.value);

  const url = window.URL.createObjectURL(resp.data);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "job-positions.xlsx");
  document.body.appendChild(link);
  link.click();
  link.remove();
  download.value = false;
};

const edit = (item, event) => {
  const check = checkClickEvent(event);
  if (check) {
    router.push({
      path: `/company/job-position/edit/${item}`,
    });
  }
};

const jobPositionsDebounced = debounce(getJobPositions, 500);

watch(
  pagination,
  () => {
    if (!isFetching.value) {
      getJobPositions();
    }
  },
  {
    deep: true,
  }
);

watch(
  params,
  () => {
    jobPositionsDebounced();
  },
  {
    deep: true,
  }
);

const headers = [
  {
    title: "Actions",
    key: "actions",
    align: "left",
    sortable: false,
    fixed: true,
  },
  { title: "Name", key: "name", width: "300px" },
  { title: "Created", key: "createdAt", width: "300px" },
  { title: "Company", key: "companyName", width: "200px" },
  { title: "Status", key: "status" },
  { title: "Number of positions", key: "numberOfPositions" },
  { title: "Selected beneficiaries", key: "selectedBeneficiaries" },
  { title: "Matched beneficiaries", key: "matchedBeneficiaries" },
  { title: "Hired beneficiaries", key: "hiredBeneficiaries" },
  { title: "Link", key: "link", align: "center" },
  { title: "Parent location", key: "parentLocationName" },
  { title: "Location", key: "locationName" },
  { title: "Odyssea Branch", key: "branch" },
  { title: "Contract Type", key: "contractType" },
  { title: "Working Shifts", key: "workingShifts" },
  { title: "Workweek Schedule", key: "workWeekSchedule" },
  { title: "Salary Range", key: "salaryRange" },
  { title: "Responsibilities", key: "responsibilities" },
  { title: "Previous Experience", key: "previousExperience" },
  { title: "Offers Training", key: "offersTraining" },
  { title: "Required Languages", key: "requiredLanguages" },
  { title: "Nationalities", key: "nationalities" },
  { title: "Gender", key: "gender" },
  { title: "Age", key: "age" },
  { title: "Requires Driving License", key: "requiresDrivingLicense" },
  { title: "Driving License", key: "drivingLicense" },
  { title: "Open to PwD", key: "openToPwD" },
  { title: "Soft Skills", key: "softSkills" },
  { title: "Contact through Whatsapp", key: "contactWhatsapp" },
  { title: "Additional Info", key: "additionalInfo" },
  { title: "Offers Transportation", key: "offersTransportation" },
  { title: "Communication Responsible", key: "responsibleUser" },
  { title: "Logged By", key: "loggedBy" },
];

// onBeforeMount(() => {
//   jobPositionStore.resetAction();
// });

onMounted(() => {
  getJobPositions();
});
</script>

<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <h1 class="mb-10 page-title">Job Positions Overview</h1>
        <Filters variant="company" />
        <div class="d-flex justify-space-between align-center mb-5">
          <h3>Total: {{ pagination.totalItems }}</h3>
          <v-btn
            color="company"
            :loading="download"
            :disabled="download"
            @click="exportTable()"
          >
            <div class="d-flex align-center">
              <v-icon>mdi-file-excel</v-icon>
              <span>Export</span>
            </div>
          </v-btn>
        </div>
        <v-data-table
          :items="data"
          :headers="headers"
          :items-per-page="pagination.pageSize"
          :hide-default-footer="true"
          :loading="isFetching"
          class="job-position-table bordered-columns"
        >
          <template #item.name="{ item }">
            <router-link
              :to="'/company/job-position/view/' + item.id"
              class="chip-link-color-company"
            >
              <v-chip> {{ item.name }} </v-chip>
            </router-link>
          </template>

          <template #item.createdAt="{ item }">
            <span style="white-space: nowrap">{{
              formatDateTime(item.createdAt)
            }}</span>
          </template>

          <template #item.link="{ item }">
            <v-btn
              v-if="item.link"
              color="company"
              @click="openJobAd(item.link)"
            >
              Visit link
            </v-btn>
          </template>

          <template #item.actions="{ item }">
            <div class="d-flex ga-2 justify-center">
              <v-btn
                icon="mdi-pencil"
                size="x-small"
                color="company"
                @click="edit(item.id, $event)"
              />
            </div>
          </template>
        </v-data-table>
        <div class="mt-5">
          <v-pagination
            v-model="pagination.currentPage"
            :length="pagination.pageCount"
            :total-visible="5"
          />
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<style lang="scss" scoped>
h1 {
  color: rgb(var(--v-theme-company));
}
.v-table {
  table-layout: fixed;

  td,
  th {
    border-right: thin solid
      rgba(var(--v-border-color), var(--v-border-opacity));
    white-space: normal;
  }
}
</style>
